'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { interpolateStringWithData } from '../../../../../utils/src/string';
import { useBreakpoints } from '../../../lib/hooks/useBreakpoints';
import { useIsClient } from '../../../lib/hooks/useIsClient';
import { c } from '../../../utils/tailwind-merge';
import { Separator } from '../../atoms/Separator/Separator';
import { Banner } from '../../molecules/Banner';
import { Gallery } from '../../molecules/Gallery/Gallery';
import { ProductBreadcrumbs } from '../../molecules/ProductBreadcrumbs/ProductBreadcrumbs';
import { EntitlementConfirmation } from '../../organisms/EntitlementConfirmation/EntitlementConfirmation';
import { ProductInfo } from '../../organisms/ProductInfo/ProductInfo';
import { ProductNutritionLabels } from '../../organisms/ProductNutritionLabels/ProductNutritionLabels';
import { ProductTabs } from '../../organisms/ProductTabs/ProductTabs';
import { ProductTabValue } from '../../organisms/ProductTabs/ProductTabs.types';
import { PurchasedItem } from '../../organisms/PurchasedItem';
import { RelatedKeywords } from '../../organisms/RelatedKeywords/RelatedKeywords';
import {
  ProductStatus,
  UpmProductDetailTemplateProps,
} from './UpmProductDetailTemplate.types';

export const UpmProductDetailTemplate = ({
  activeSheetIndex,
  breadcrumbs,
  purchasedItem,
  currentTab,
  entitlementConfirmation,
  ownedDate,
  status,
  gallery,
  productInfo,
  nutritionLabels,
  tabs,
  relatedKeywords,
  wasEntitled,
  microcopy,
  className = '',
  previewMode = false,
  canSubmitReview,
  showDeleteReviewError = false,
  onClickChipList,
  onClickProductInfo,
  onOpenInUnityFromEntitlement,
  onGoToMyAssets,
  onGoToHomepage,
  onSeeOwnedPackages,
  onOpenInUnityFromPackage,
  onCloseFromEntitlement,
  onHandleTabChange,
  onOpenCookieSettings,
  onAccordionChange,
  onFilterReviews,
  onFilter,
  onSort,
  onPageChange,
  onSubmitReview,
  onSubmitReport,
  onEmailPublisher,
  onEditReview,
  onHelpfulVotesReview,
  onDeleteReview,
  onFavorite,
  onClickReviewsLink,
  onDismiss,
  onClickReadMore,
  onCloseSheet,
  onOpenSheet,
}: UpmProductDetailTemplateProps) => {
  const isClient = useIsClient();
  const breakpoints = useBreakpoints();
  const [activeTab, setActiveTab] = useState<ProductTabValue>(currentTab);
  const [isProductInfoSticky, setIsProductInfoSticky] = useState(false);
  const [hideSticky, setHideSticky] = useState(false);
  const isDeprecated = status === ProductStatus.Deprecated;

  // Sentinel for sticky Header
  const desktopSentinelRef = useRef<HTMLDivElement>(null);
  const mobileSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isClient || !breakpoints) return;

    // Observer for sticky header activation (sentinel-based)
    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        setIsProductInfoSticky(
          !entry.isIntersecting && entry.boundingClientRect.top < 0
        );
      },
      { threshold: 0 }
    );

    if (breakpoints.isMobile) {
      if (mobileSentinelRef.current) {
        stickyObserver.observe(mobileSentinelRef.current);
      }
    } else {
      if (desktopSentinelRef.current) {
        stickyObserver.observe(desktopSentinelRef.current);
      }
    }

    // Observer for hiding sticky header when footer is visible (mobile only)
    let footerObserver: IntersectionObserver | null = null;
    if (breakpoints.isMobile) {
      const footerEl = document.getElementById('layoutFooter');
      if (footerEl) {
        footerObserver = new IntersectionObserver(
          ([entry]) => {
            setHideSticky(entry.isIntersecting);
          },
          { threshold: 0.1 }
        );
        footerObserver.observe(footerEl);
      }
    }

    return () => {
      stickyObserver.disconnect();
      footerObserver?.disconnect();
    };
  }, [isClient, breakpoints]);

  const handleTabChange = useCallback(
    (tab: ProductTabValue) => {
      setActiveTab(tab);
      onHandleTabChange(tab);
    },
    [onHandleTabChange]
  );

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  const renderPurchasedItemAlert = () => {
    if (ownedDate && canSubmitReview && microcopy.purchasedItem) {
      return (
        <PurchasedItem
          onEmailPublisher={onEmailPublisher}
          onSubmitReview={onSubmitReview}
          microcopy={microcopy.purchasedItem}
          {...purchasedItem}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (showDeleteReviewError) {
      window.scrollTo(0, 0);
    }
  }, [showDeleteReviewError]);

  const renderDesktop = () => (
    <section className="hidden pb-20 lg:block">
      {wasEntitled && (
        <EntitlementConfirmation
          {...entitlementConfirmation}
          microcopy={microcopy.entitlementConfirmation}
          onGoToMyAssets={onGoToMyAssets}
          onOpenInUnityFromEntitlement={onOpenInUnityFromEntitlement}
          onSeeOwnedPackages={onSeeOwnedPackages}
          onCloseFromEntitlement={onCloseFromEntitlement}
        />
      )}

      {isProductInfoSticky && !hideSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
          onFavorite={onFavorite}
        />
      )}

      {showDeleteReviewError && (
        <Banner
          title={microcopy.banner?.title || ''}
          description={microcopy.banner?.description || ''}
          dismiss={microcopy.banner?.dismiss || ''}
          onDismiss={onDismiss}
          additionalInfo={microcopy.banner?.additionalInfo || ''}
          style="fill"
          className="mb-5"
        />
      )}

      {renderPurchasedItemAlert()}
      <ProductBreadcrumbs {...breadcrumbs} className="mb-1" />

      {ownedDate && isDeprecated && (
        <Banner
          title={microcopy.deprecatedBanner.title}
          description={microcopy.deprecatedBanner.description}
          dismiss={microcopy.deprecatedBanner.dismiss}
          onDismiss={onGoToHomepage}
          cta={microcopy.deprecatedBanner.cta}
          onCta={onGoToMyAssets}
          additionalInfo={interpolateStringWithData(
            microcopy.deprecatedBanner?.additionalInfo || '',
            { ownedDate }
          )}
          style="fill"
          type="info"
          className="md:mb-5"
        />
      )}

      <div className="grid grid-cols-[minmax(0,_1fr)_396px] items-start gap-6">
        <div className="flex min-w-0 flex-col gap-6 self-start">
          <Gallery
            {...gallery}
            microcopy={microcopy.gallery}
            position="details"
            aspectRatio="aspect-[3/2]"
            onOpenCookieSettings={onOpenCookieSettings}
            className={c({ 'opacity-60 grayscale': isDeprecated })}
          />
          <ProductTabs
            onFilterReviews={onFilterReviews}
            onSubmitReview={onSubmitReview}
            onEmailPublisher={onEmailPublisher}
            onHelpfulVotesReview={onHelpfulVotesReview}
            onEditReview={onEditReview}
            onHelpfulVotesReply={() => {}}
            onEditReply={() => {}}
            onDeleteReply={() => {}}
            onSubmitReply={() => {}}
            onFilter={onFilter}
            onSort={onSort}
            onPageChange={onPageChange}
            onSubmitReport={onSubmitReport}
            {...tabs}
            microcopy={microcopy.tabs}
            value={activeTab}
            onValueChange={handleTabChange}
            onOpenInUnityFromPackage={
              previewMode ? undefined : onOpenInUnityFromPackage
            }
            onAccordionChange={onAccordionChange}
            onDeleteReview={onDeleteReview}
          />
          {relatedKeywords.items.length > 0 && (
            <>
              <Separator className="mt-16" />
              <RelatedKeywords
                {...relatedKeywords}
                microcopy={microcopy.relatedKeywords}
                onClickChipList={onClickChipList}
              />
            </>
          )}
        </div>

        <aside className="flex h-full flex-col gap-6 lg:shrink-0">
          <div
            className={c(
              'transition-opacity duration-200',
              isProductInfoSticky ? 'pointer-events-none invisible' : 'visible'
            )}
          >
            <ProductInfo
              {...productInfo}
              variant="default"
              microcopy={microcopy.productInfo}
              onClickProductInfo={previewMode ? undefined : onClickProductInfo}
              onFavorite={onFavorite}
              onClickReviewsLink={onClickReviewsLink}
              reviews={productInfo.reviews}
            />
          </div>

          <div
            ref={desktopSentinelRef}
            className="h-px w-full"
            aria-hidden="true"
          />

          <ProductNutritionLabels
            {...nutritionLabels}
            microcopy={microcopy.nutritionLabels}
            onClickReadMore={onClickReadMore}
          />
        </aside>
      </div>
    </section>
  );

  const renderMobile = () => (
    <section
      className={c(
        'grid grid-cols-1 gap-6 lg:hidden',
        isProductInfoSticky && 'pb-20'
      )}
    >
      {isProductInfoSticky && !hideSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
          onFavorite={onFavorite}
        />
      )}

      {showDeleteReviewError && (
        <Banner
          title={microcopy.banner?.title || ''}
          description={microcopy.banner?.description || ''}
          dismiss={microcopy.banner?.dismiss || ''}
          onDismiss={onDismiss}
          style="fill"
        />
      )}

      {renderPurchasedItemAlert()}
      <ProductBreadcrumbs {...breadcrumbs} />

      {ownedDate && isDeprecated && (
        <Banner
          title={microcopy.deprecatedBanner.title}
          description={microcopy.deprecatedBanner.description}
          dismiss={microcopy.deprecatedBanner.dismiss}
          onDismiss={onGoToHomepage}
          cta={microcopy.deprecatedBanner.cta}
          onCta={onGoToMyAssets}
          additionalInfo={interpolateStringWithData(
            microcopy.deprecatedBanner?.additionalInfo || '',
            { ownedDate }
          )}
          style="fill"
          type="info"
        />
      )}

      <div
        className={c(
          isProductInfoSticky ? 'pointer-events-none invisible' : 'visible'
        )}
      >
        <ProductInfo
          {...productInfo}
          variant="default"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
          onFavorite={onFavorite}
          onClickReviewsLink={onClickReviewsLink}
        />
      </div>

      <div ref={mobileSentinelRef} className="h-px w-full" aria-hidden="true" />

      <Gallery
        {...gallery}
        position="details"
        microcopy={microcopy.gallery}
        aspectRatio="aspect-[3/2]"
        onOpenCookieSettings={onOpenCookieSettings}
        className={c({ 'opacity-60 grayscale': isDeprecated })}
      />
      <ProductTabs
        onFilterReviews={onFilterReviews}
        onSubmitReview={onSubmitReview}
        onEmailPublisher={onEmailPublisher}
        onHelpfulVotesReview={onHelpfulVotesReview}
        onEditReview={onEditReview}
        onHelpfulVotesReply={() => {}}
        onEditReply={() => {}}
        onDeleteReply={() => {}}
        onSubmitReply={() => {}}
        onFilter={onFilter}
        onSort={onSort}
        onPageChange={onPageChange}
        onSubmitReport={onSubmitReport}
        {...tabs}
        microcopy={microcopy.tabs}
        value={activeTab}
        activeSheetIndex={activeSheetIndex}
        onValueChange={onHandleTabChange}
        onOpenInUnityFromPackage={
          previewMode ? undefined : onOpenInUnityFromPackage
        }
        onAccordionChange={onAccordionChange}
        onDeleteReview={onDeleteReview}
        onCloseSheet={onCloseSheet}
        onOpenSheet={onOpenSheet}
        className="px-4"
      />
      <div>
        <ProductNutritionLabels
          {...nutritionLabels}
          microcopy={microcopy.nutritionLabels}
          onClickReadMore={onClickReadMore}
          className="px-4"
        />
        {relatedKeywords.items.length > 0 && (
          <>
            <Separator />
            <RelatedKeywords
              {...relatedKeywords}
              microcopy={microcopy.relatedKeywords}
              className="px-4"
              onClickChipList={onClickChipList}
            />
          </>
        )}
      </div>
    </section>
  );

  return (
    <div className={className}>
      {renderDesktop()}
      {renderMobile()}
    </div>
  );
};
